import { call, put, takeEvery, all, select } from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import { getEntityCall } from '../entities/sagas';
import axios from 'axios';
import ObjectUtils from "../shared/utils/ObjectUtils";
import { translateError } from '../shared/utils/Errors';
import { getUserPageRefresh } from '../auth/sagas';

const controllerUrl = '/roles/';

const searchRolesCall = searchFields => new Promise((resolve, reject) => {
        axios.get(controllerUrl + 'search', {
            params: {
                ...searchFields,
            }
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const getRoleCall = (id) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + id).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const createRoleCall = (roleData) => new Promise((resolve, reject) => {
    axios.post(controllerUrl, roleData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const editRoleCall = (roleId, roleData) => new Promise((resolve, reject) => {
    axios.put(controllerUrl + roleId, roleData).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const deleteRoleCall = roleId => new Promise((resolve, reject) => {
    axios.delete(controllerUrl + roleId).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const getRoleListCall = filter => new Promise((resolve, reject) => {
        axios.get(controllerUrl + 'list', {
            params: filter
        }).then(
            response => resolve(response.data),
            error => reject(error)
        );
});

const permissionsToArray = permissionsList => {
    let permissionsArray = [];

    permissionsList.forEach(group => {
        group.permissions.forEach(p => {
            if (p.value === true) {
                permissionsArray.push(p.name);
            }
        })
    })
    return permissionsArray;
};

const arrayToPermissions = (permissionsArray, permissionsList) => {

    permissionsList.forEach(group => {
        group.permissions.forEach(p => {
            if (permissionsArray.includes(p.name)) {
                p.value = true;
            }
        })
    })
    return permissionsList;
};


function* getRoleListRequest({ filter }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const roleList = yield call(() => getRoleListCall(filter));
        yield put({
            type: actionTypes.GET_ROLE_LIST_SUCCESS,
            payload: roleList.map(role => ({ value: role.id, label: role.name }))
        });
    } catch (e) {
    }
}

function* searchRolesRequest({ searchFields }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const searchResults = yield call(() => searchRolesCall(searchFields));
        const entityIds = searchResults.content.map(role => role.entityId);
        const uniqueEntityIds = [...new Set(entityIds)];
        const entities = yield all(
            uniqueEntityIds
                .filter(id => !ObjectUtils.isNil(id))
                .map(id => call(getEntityCall, id))
        );

        if (searchResults.sort[0].property === "entity.name") {
            searchResults.sort[0].property = "entityName"
        }

        const searchResultsWithEntities = {
            ...searchResults,
            content: searchResults.content.map(
                role => ({
                    ...role,
                    entity: entities.find(e => e.id === role.entityId)
                })
            )
        };
        yield put({
            type: actionTypes.ROLE_SEARCH_SUCCESS,
            payload: searchResultsWithEntities
        });
    } catch (e) {
        yield put({
            type: actionTypes.ROLE_SEARCH_FAILED
        });
    }
}

function* getRoleRequest({ roleId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const role = yield call(getRoleCall, roleId);
        const permissionsList = yield call(() => getPermissionsListCall());
        let tailoredPermissions = createBlankPermissionsList(permissionsList);
        role.permissionsList = arrayToPermissions(role.permissions, tailoredPermissions);
        role.entity = role.entityId;

        yield put({
            type: actionTypes.GET_ROLE_SUCCESS,
            payload: role
        });
    } catch (e) {
    }
}

function* createRoleRequest({ roleData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        roleData.permissions = permissionsToArray(roleData.permissionsList);
        roleData.entityId = roleData.entity;
        yield call(() => createRoleCall(roleData));

        yield put({
            type: actionTypes.CREATE_ROLE_SUCCESS
        })
    } catch ({ response }) {
        yield put({
            type: actionTypes.CREATE_ROLE_FAILED,
            payload: translateError('roles', response.status, response.data.status)
        });
    }
}

function* editRoleRequest({ roleId, roleData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        roleData.permissions = permissionsToArray(roleData.permissionsList);
        roleData.entityId = roleData.entity;
        yield call(editRoleCall, roleId, roleData);

        yield put({
            type: actionTypes.EDIT_ROLE_SUCCESS
        })
    } catch ({ response }) {
        yield put({
            type: actionTypes.EDIT_ROLE_FAILED,
            payload: translateError('roles', response.status, response.data.status)
        })
    }
}

function* deleteRoleRequest({ roleId }) {
    try {
        yield call(deleteRoleCall, roleId);

        yield put({
            type: actionTypes.DELETE_ROLE_SUCCESS
        })
    } catch ({ response }) {
        yield put({
            type: actionTypes.DELETE_ROLE_FAILED,
            payload: translateError('roles', response.status, response.data.status)
        });
    }
}

const getPermissionsListCall = (filter) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'permissions', {
        params: filter
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});


const createBlankPermissionsList = permissionsList => {
    let tailoredPermissions = [];
    permissionsList.forEach(p => {
        const permissionCategoryIndex = tailoredPermissions.findIndex(permission => permission.groupKey === p.category);
        if (permissionCategoryIndex === -1) {
            tailoredPermissions.push({
                groupKey: p.category,
                permissions: [{
                    name: p.name,
                    value: false
                }]
            });
        } else {
            tailoredPermissions[permissionCategoryIndex].permissions.push({
                name: p.name,
                value: false
            })
        }
    });
    return tailoredPermissions;
};


function* getPermissionsListRequest({ filter }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const permissionsList = yield call(() => getPermissionsListCall(filter));
        let tailoredPermissions = createBlankPermissionsList(permissionsList);

        yield put({
            type: actionTypes.GET_PERMISSION_LIST_SUCCESS,
            payload: tailoredPermissions
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_PERMISSION_LIST_FAILED
        });
    }
}

const roleSagas = [
    takeEvery(actionTypes.GET_ROLE_LIST_REQUEST, getRoleListRequest),
    takeEvery(actionTypes.ROLE_SEARCH_REQUEST, searchRolesRequest),
    takeEvery(actionTypes.GET_ROLE_REQUEST, getRoleRequest),
    takeEvery(actionTypes.CREATE_ROLE_REQUEST, createRoleRequest),
    takeEvery(actionTypes.EDIT_ROLE_REQUEST, editRoleRequest),
    takeEvery(actionTypes.DELETE_ROLE_REQUEST, deleteRoleRequest),
    takeEvery(actionTypes.GET_PERMISSION_LIST_REQUEST, getPermissionsListRequest),
];

export {
    roleSagas,
    getRoleCall
};